import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Billboard, Line, Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';
import { resolveFlagFlyRotation, resolveFlagPostTransform } from '../_pure/flagPostTransform.js';
import { projectedLabelFontPixels } from '../_pure/labelProximity.js';
import { resolveLabelFramePresentation } from '../_pure/labelRenderPlan.js';
const WORLD_POSITION = new THREE.Vector3();
const CAMERA_ROTATION = new THREE.Quaternion();
/** Samples live transforms and renders the pure label plan. */
export function ItemLabel(props) {
    const { plan, proximity, cameraProximityFade } = props;
    const { label } = plan;
    const contentRef = useRef(null);
    const postRef = useRef(null);
    const flyRef = useRef(null);
    const fadedMaterials = useRef(new Map());
    const registerMaterial = (face, baseOpacity) => (material) => {
        if (material === null) {
            fadedMaterials.current.delete(face);
            return;
        }
        material.transparent = true;
        fadedMaterials.current.set(face, { material, baseOpacity });
    };
    useFrame(({ camera, size }) => {
        const post = postRef.current;
        if (post?.parent) {
            post.parent.updateWorldMatrix(true, false);
            resolveFlagPostTransform(post.parent.matrixWorld, label.position, post.matrix);
            post.matrixWorldNeedsUpdate = true;
        }
        if (label.flag && flyRef.current) {
            camera.getWorldQuaternion(CAMERA_ROTATION);
            resolveFlagFlyRotation(label.flag.postOffset, CAMERA_ROTATION, flyRef.current.quaternion);
        }
        const content = contentRef.current;
        if (!content || (proximity === undefined && cameraProximityFade === undefined && !label.flag))
            return;
        camera.getWorldPosition(WORLD_POSITION);
        const eyeX = WORLD_POSITION.x, eyeY = WORLD_POSITION.y, eyeZ = WORLD_POSITION.z;
        content.getWorldPosition(WORLD_POSITION);
        const labelDistance = Math.hypot(eyeX - WORLD_POSITION.x, eyeY - WORLD_POSITION.y, eyeZ - WORLD_POSITION.z);
        let itemDistance;
        if (cameraProximityFade !== undefined && content.parent) {
            content.parent.updateWorldMatrix(true, false);
            const matrix = content.parent.matrixWorld.elements;
            itemDistance = Math.hypot(eyeX - matrix[12], eyeY - matrix[13], eyeZ - matrix[14]);
        }
        let pixels = 0;
        if (label.flag) {
            content.getWorldPosition(WORLD_POSITION);
            WORLD_POSITION.applyMatrix4(camera.matrixWorldInverse);
            const depth = -WORLD_POSITION.z;
            pixels = projectedLabelFontPixels({
                worldFontSize: label.fontSize * content.getWorldScale(WORLD_POSITION).y,
                projectionScale: camera.projectionMatrix.elements[5],
                viewportHeight: size.height,
                cameraDepth: depth,
                perspective: camera instanceof THREE.PerspectiveCamera,
            });
        }
        const presentation = resolveLabelFramePresentation({
            label, proximity, cameraProximityFade, labelDistance, itemDistance, projectedFontPixels: pixels,
        });
        content.visible = presentation.visible;
        for (const [face, entry] of fadedMaterials.current) {
            entry.material.opacity = entry.baseOpacity * (face === 'background' ? presentation.backgroundFactor : presentation.textFactor);
        }
    });
    const { textRenderOrder } = plan;
    const renderTextFace = (face) => (_jsx("group", { position: face.position, rotation: face.rotation, children: _jsxs(Text, { color: label.color, fontSize: label.fontSize, anchorX: "center", anchorY: "middle", depthOffset: -1, ...(textRenderOrder === undefined ? {} : { renderOrder: textRenderOrder }), children: [_jsx("meshBasicMaterial", { ref: registerMaterial(face.key, label.opacity), color: label.color, side: THREE.FrontSide, transparent: label.opacity < 1 || proximity !== undefined || !!label.flag, opacity: plan.initialTextOpacity, depthTest: label.depthTest, depthWrite: false, toneMapped: false }), label.text] }) }, face.key));
    const content = (_jsxs("group", { ref: contentRef, position: plan.contentPosition, rotation: label.rotation, children: [plan.border ? (_jsx(Line, { points: plan.border.points, color: label.color, lineWidth: 1, transparent: true, opacity: label.opacity, depthTest: label.depthTest, renderOrder: plan.border.renderOrder })) : null, plan.background ? (_jsxs("mesh", { position: plan.background.position, ...(label.renderOrder === null ? {} : { renderOrder: label.renderOrder }), children: [_jsx("planeGeometry", { args: [plan.background.width, plan.background.height] }), _jsx("meshBasicMaterial", { ref: registerMaterial('background', plan.background.opacity), color: plan.background.color, transparent: true, opacity: plan.background.opacity, depthTest: label.depthTest, depthWrite: false })] })) : null, plan.faces.map(renderTextFace)] }));
    if (plan.post) {
        return (_jsxs("group", { ref: postRef, matrixAutoUpdate: false, children: [_jsx(Line, { points: plan.post.points, color: label.color, lineWidth: 1.5, transparent: true, opacity: label.opacity, depthTest: label.depthTest, renderOrder: plan.post.renderOrder }), plan.post.prongs ? _jsx(Line, { points: plan.post.prongs, segments: true, color: label.color, lineWidth: 1.5, transparent: true, opacity: label.opacity, depthTest: label.depthTest, renderOrder: plan.post.renderOrder }) : null, _jsx("group", { ref: flyRef, children: content })] }));
    }
    if (!label.billboard)
        return content;
    return (_jsx(Billboard, { follow: true, lockX: false, lockY: false, lockZ: false, children: content }));
}
